import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Package,
  Warehouse,
  Target,
  LogOut,
  CalendarDays,
  ShieldCheck,
  KeyRound,
  FileText,
  Menu,
  MoreHorizontal,
  X,
  Network,
  ScrollText,
  MapPin,
  CalendarClock,
  Settings,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const sysAdmin = (u) => u?.role_name === "Admin";
export const MODULE_ROUTES = [
  ["/clients", "clients"], ["/catalog", "catalog"], ["/inventory", "inventory"], ["/leads", "leads"],
  ["/bookings", "bookings"], ["/schedule", "bookings"], ["/quotations", "quotations"],
];
const needs = (m) => (u) => can(u, m, "view");

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients", requires: needs("clients") },
  { to: "/catalog", label: "Machine Catalog", icon: Package, testid: "nav-catalog", requires: needs("catalog") },
  { to: "/inventory", label: "Inventory", icon: Warehouse, testid: "nav-inventory", requires: needs("inventory") },
  { to: "/leads", label: "Leads", icon: Target, testid: "nav-leads", requires: needs("leads") },
  { to: "/bookings", label: "Demo Bookings", icon: CalendarDays, testid: "nav-bookings", requires: needs("bookings") },
  { to: "/schedule", label: "Schedule", icon: CalendarClock, testid: "nav-schedule", requires: needs("bookings") },
  { to: "/quotations", label: "Quotations", icon: FileText, testid: "nav-quotations", requires: needs("quotations") },
];

const masterItems = [
  { to: "/team", label: "Users", icon: ShieldCheck, testid: "nav-team", requires: needs("team") },
  { to: "/org-chart", label: "Org Chart", icon: Network, testid: "nav-org-chart", requires: needs("team") },
  { to: "/roles", label: "Roles & Permissions", icon: KeyRound, testid: "nav-roles", requires: needs("roles") },
  { to: "/masters/regions", label: "Regions / Hubs", icon: MapPin, testid: "nav-regions", requires: sysAdmin },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText, testid: "nav-audit-log", requires: sysAdmin },
  { to: "/masters/settings", label: "Settings", icon: Settings, testid: "nav-settings", requires: sysAdmin },
];

// Bottom-tab layout (thumb-reach for field use). "More" opens a drawer with the rest.
const bottomTabs = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard, testid: "mnav-dashboard" },
  { to: "/leads", label: "Leads", icon: Target, testid: "mnav-leads", requires: needs("leads") },
  { to: "/schedule", label: "Schedule", icon: CalendarClock, testid: "mnav-schedule", requires: needs("bookings") },
  { to: "/quotations", label: "Quotes", icon: FileText, testid: "mnav-quotations", requires: needs("quotations") },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const allowed = (i) => !i.requires || i.requires(user);
  const visibleNav = navItems.filter(allowed);
  const visibleTabs = bottomTabs.filter(allowed);
  const overflowNav = [
    ...visibleNav.filter((n) => !bottomTabs.some((t) => t.to === n.to)),
    ...masterItems.filter((i) => i.requires(user)),
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ---------------------------- Desktop sidebar ---------------------------- */}
      <aside
        className="hidden md:flex fixed top-0 left-0 h-screen w-64 bg-white border-r border-slate-200 flex-col"
        data-testid="sidebar"
      >
        <div className="px-4 py-5 border-b border-slate-200">
          <img src="/hubai-logo.webp" alt="HubAi ERP" className="w-full max-h-16 object-contain" />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
          {masterItems.some((i) => i.requires(user)) && (
            <div className="pt-3 mt-3 border-t border-slate-100">
              <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Masters</div>
              {masterItems.filter((i) => i.requires(user)).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                      isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        <div className="border-t border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate" data-testid="user-name">{user?.name || "User"}</div>
              <div className="text-xs text-slate-500 truncate" data-testid="user-email">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-md transition-colors duration-150 border border-slate-200"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* ---------------------------- Mobile top bar ---------------------------- */}
      <header
        className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4"
        data-testid="mobile-topbar"
      >
        <div className="flex items-center min-w-0 flex-1">
          <img src="/hubai-logo.webp" alt="HubAi ERP" className="max-h-11 max-w-[180px] w-auto object-contain" />
        </div>
        <button
          onClick={() => setDrawerOpen(true)}
          className="w-11 h-11 flex items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 active:bg-slate-200"
          data-testid="mobile-hamburger"
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6" />
        </button>
      </header>

      {/* Full-menu drawer from hamburger */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-[80vw] max-w-sm p-0 flex flex-col">
          <SheetHeader className="px-5 py-4 border-b border-slate-200">
            <SheetTitle className="text-left">
              <img src="/hubai-logo.webp" alt="HubAi ERP" className="h-12 w-auto object-contain" />
            </SheetTitle>
          </SheetHeader>
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
            {visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setDrawerOpen(false)}
                data-testid={`drawer-${item.testid}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 rounded-md text-[15px] font-medium min-h-11 ${
                    isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-700 active:bg-slate-100"
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
            {masterItems.some((i) => i.requires(user)) && (
              <div className="pt-3 mt-3 border-t border-slate-100">
                <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Masters</div>
                {masterItems.filter((i) => i.requires(user)).map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setDrawerOpen(false)}
                    data-testid={`drawer-${item.testid}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 rounded-md text-[15px] font-medium min-h-11 ${
                        isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-700 active:bg-slate-100"
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )}
          </nav>
          <div className="border-t border-slate-200 p-4 space-y-2">
            <div>
              <div className="text-sm font-semibold text-slate-900 truncate">{user?.name || "User"}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            <Button
              variant="outline"
              className="w-full min-h-11"
              onClick={() => { setDrawerOpen(false); handleLogout(); }}
              data-testid="drawer-logout"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ---------------------------- Mobile bottom tab bar ---------------------------- */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-bottom-nav"
      >
        <div className="grid h-16" style={{ gridTemplateColumns: `repeat(${visibleTabs.length + 1}, minmax(0, 1fr))` }}>
          {visibleTabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${
                  isActive ? "text-indigo-700" : "text-slate-500 active:text-indigo-700"
                }`
              }
            >
              <t.icon className="w-5 h-5" />
              {t.label}
            </NavLink>
          ))}
          <button
            onClick={() => setMoreOpen(true)}
            data-testid="mnav-more"
            className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${
              overflowNav.some((n) => n.to === location.pathname) ? "text-indigo-700" : "text-slate-500 active:text-indigo-700"
            }`}
          >
            <MoreHorizontal className="w-5 h-5" />
            More
          </button>
        </div>
      </nav>

      {/* "More" drawer for overflow items */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl p-0 max-h-[80vh]">
          <SheetHeader className="px-5 py-4 border-b border-slate-100 flex-row items-center justify-between">
            <SheetTitle>More</SheetTitle>
            <button
              onClick={() => setMoreOpen(false)}
              className="w-9 h-9 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </SheetHeader>
          <div className="p-4 grid grid-cols-3 gap-3 overflow-y-auto">
            {overflowNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMoreOpen(false)}
                data-testid={`more-${item.testid}`}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-2 p-3 rounded-xl border ${
                    isActive
                      ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                      : "bg-white border-slate-200 text-slate-700 active:bg-slate-50"
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[12px] font-medium text-center leading-tight">{item.label}</span>
              </NavLink>
            ))}
            <button
              onClick={() => { setMoreOpen(false); handleLogout(); }}
              className="flex flex-col items-center gap-2 p-3 rounded-xl border border-rose-200 bg-white text-rose-600 active:bg-rose-50"
              data-testid="more-logout"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-[12px] font-medium">Sign out</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ---------------------------- Main content ---------------------------- */}
      <main className="md:ml-64 min-h-screen pt-14 md:pt-0 pb-20 md:pb-0">
        <div className="p-4 md:p-8 md:max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
